/**
 *
 * QuestionGrid
 *
 */

// @flow

import { Button, Cell, FieldError, Grid, Label, UnsafeHtml } from '@fil-global/eiswurfel';
import classnames from 'classnames';
import _noop from 'lodash/noop';
import type { Node } from 'react';
import React, { cloneElement, useState } from 'react';

import { useStaticLabelsContext } from '../../global/context';
import { RadioGroupField, TextInputField } from '../FormControls';
import Seperator from '../Seperator';
import type { Props } from './types';

const QuestionGrid = ({
  children,
  extraClasses,
  fieldName,
  fieldValue,
  goToEditMode,
  goToNextQuestion,
  hideNextButton,
  inputComponent,
  inputConfigs,
  isEditMode,
  notes,
  onValueChange,
  label,
  questionNumber,
  validateSchema,
}: Props): Node => {
  const staticLabels = useStaticLabelsContext();
  const [errors, setErrors] = useState(null);

  const isCustomController = inputComponent === 'custom';

  const onChange = event => {
    let { value } = event.currentTarget;

    setErrors(null);

    if (validateSchema) {
      try {
        value = validateSchema.validateSync(value);
      } catch (ferrors) {
        setErrors(ferrors.message);
      }
    }

    onValueChange(fieldName, value);
  };

  const onNextClick = () => {
    onValueChange(fieldName, fieldValue);

    if (!errors) {
      goToNextQuestion();
    }
  };

  const onEditClick = () => {
    goToEditMode();
  };

  const renderInputComponent = () => {
    const configs = {
      ...inputConfigs,
      disabled: !isEditMode,
      id: fieldName,
      name: fieldName,
      onChange,
      value: fieldValue,
    };

    if (inputComponent === 'radio') {
      return <RadioGroupField {...configs} />;
    }
    return <TextInputField {...configs} />;
  };

  return (
    <Grid extraClasses={classnames('question-grid', 'margin-bottom-48', extraClasses)}>
      <Cell extraClasses="margin-bottom-32 title transparent-light">
        <Label htmlFor={fieldName}>
          <strong>{`${questionNumber}. ${label}`}</strong>
        </Label>
      </Cell>

      <Cell extraClasses="margin-bottom-32 transparent-semi">
        {!isCustomController ? (
          <>
            {renderInputComponent()}
            {errors && <FieldError>{errors}</FieldError>}
          </>
        ) : (
          <>{cloneElement(children, { fieldName, isEditMode })}</>
        )}

        {isEditMode && notes && (
          <Cell extraClasses="margin-bottom-24">
            <UnsafeHtml>{notes}</UnsafeHtml>
          </Cell>
        )}
      </Cell>

      {isEditMode && !hideNextButton && (
        <Cell extraClasses="margin-bottom-32">
          <Button onClick={onNextClick} theme="secondary">
            {staticLabels.buttons.next}
          </Button>
        </Cell>
      )}

      {!isEditMode && (
        <Cell extraClasses="margin-bottom-32">
          <Button onClick={onEditClick} theme="link">
            {staticLabels.buttons.edit}
          </Button>
        </Cell>
      )}

      <Seperator />
    </Grid>
  );
};

QuestionGrid.defaultProps = {
  hideNextButton: false,
  goToEditMode: _noop,
  goToNextQuestion: _noop,
  inputConfigs: {},
  onValueChange: _noop,
};

export default QuestionGrid;
